import { SessionKindGroupName } from 'types/models/SessionsPageData';
import { t } from 'utilities/TranslationUtils';
import { kickNoun } from 'utilities/kickUtils';
import PullDirectionBall from './PullDirectionBall';
import './KickStatsSummaryRow.css';

type Props = {
  /** Pre-aggregated (server- or selection-computed) stats — this component only renders them. */
  totalKicks: number;
  onTargetPct: number | null;
  avgBallSpeed: number | null;
  /** Optional secondary averages. A tile is rendered only when its value is provided (not undefined). */
  avgSpinRate?: number | null;
  avgPullDirection?: number | null;
  /** Average spin-axis angle (deg) for the pull-direction ball's axis line. */
  avgSpinAxis?: number | null;
  /** Wall-crossing height tile is free-kick-only; ignored for other kinds. */
  avgWallCrossingHeight?: number | null;
  kind?: SessionKindGroupName;
  /** When true, the row reflects only the drag-selected kicks and pulses in the selection colour. */
  selectionActive?: boolean;
  selectedCount?: number;
  onClearSelection?: () => void;
  /** Optional total session count tile (rendered first when provided). */
  totalSessions?: number | null;
  /** Compact mode for top-of-page summaries: sessions + kicks/throws + on-target only — ball speed
   *  (like spin/pull/wall) is hidden in compact mode. */
  compactMode?: boolean;
};

const KickStatsSummaryRow = ({
  totalKicks, onTargetPct, avgBallSpeed, avgSpinRate, avgPullDirection, avgSpinAxis, avgWallCrossingHeight,
  kind, selectionActive = false, selectedCount, onClearSelection, totalSessions = null, compactMode = false,
}: Props) => {
  // Keep rendering even when totalKicks is 0 (e.g., when "Show only favorites" filters to zero kicks).
  // This preserves the stat cells' layout, showing them with dashed appearance to indicate the data
  // is unavailable due to active filters. Removing this caused regression where users thought the
  // entire card was broken when they filtered to empty results.

  const onTargetPercentage = onTargetPct != null ? Math.round(onTargetPct) : null;

  // Show spin rate tile for all kinds except Penalty (spin is withheld by services for penalties as
  // values are deemed inaccurate). For other kinds, show the tile even when no data (null), displaying
  // "—" with dashed styling to match the empty-state behavior of other cells.
  const showSpinTile = kind !== 'Penalty';
  // Show pull direction tile for all kinds (will display "—" when unavailable).
  const showPullTile = true;
  // Show wall-crossing height tile for free kicks only (will display "—" when unavailable).
  const showWallTile = kind === 'FreeKick';

  const renderTile = (
    title: string,
    valueNode: React.ReactNode,
    isAvg: boolean,
    noHoverEffect: boolean = false,
  ) => (
    <div className={`kick-stats-item${noHoverEffect ? ' kick-stats-item--no-hover' : ''}`}>
      <div className='kick-stats-title-group'>
        <div className='kick-stats-title'>{title}</div>
        <div className={`kick-stats-title-avg${isAvg ? '' : ' kick-stats-title-avg--placeholder'}`}>
          {t('sessions.statAvgLabel')}
        </div>
      </div>
      <div className='kick-stats-value-row'>{valueNode}</div>
    </div>
  );

  const valueUnit = (value: number | null, unit: string, decimals: number): React.ReactNode =>
    value != null ? (
      <>
        <div className='kick-stats-value'>{value.toFixed(decimals)}</div>
        <div className='kick-stats-unit-inline'>{unit}</div>
      </>
    ) : (
      <div className='kick-stats-value kick-stats-value--na'>—</div>
    );

  const degrees = t('common.unitDegrees');

  return (
    <div className={`kick-stats-summary-row${selectionActive ? ' kick-stats-summary-row--selection' : ''}${totalKicks === 0 ? ' kick-stats-summary-row--empty' : ''}`}>
      <div className='kick-stats-selection-note-container'>
        {selectionActive && (
          <div className='kick-stats-selection-note'>
            <span>{t('sessions.statsSelectionNote', { count: selectedCount ?? 0 })}</span>
            {onClearSelection && (
              <button type='button' className='kick-stats-selection-clear' onClick={onClearSelection}>
                {t('sessions.statsSelectionClear')}
              </button>
            )}
          </div>
        )}
      </div>

      {totalSessions != null && renderTile(
        t('sessions.fieldSessionsLabel'),
        <div className='kick-stats-value'>{totalSessions}</div>,
        false,
        compactMode,
      )}

      {renderTile(
        kickNoun(kind, true),
        <div className='kick-stats-value'>{totalKicks}</div>,
        false,
        compactMode,
      )}

      {renderTile(
        t('sessions.statOnTarget'),
        <div className='kick-stats-value'>{onTargetPercentage}%</div>,
        totalKicks > 0,
        compactMode,
      )}

      {!compactMode && renderTile(
        t('sessions.statAvgBallSpeed'),
        avgBallSpeed != null
          ? (<><div className='kick-stats-value'>{Math.round(avgBallSpeed)}</div><div className='kick-stats-unit-inline'>{t('common.unitKmh')}</div></>)
          : (<div className='kick-stats-value kick-stats-value--na'>—</div>),
        totalKicks > 0,
      )}

      {!compactMode && showSpinTile && renderTile(
        t('sessions.statAvgSpinRate'),
        valueUnit(avgSpinRate ?? null, t('common.unitRpm'), 0),
        totalKicks > 0,
      )}

      {!compactMode && showPullTile && (
        <div className='kick-stats-item kick-stats-item--pull'>
          <div className='kick-stats-title-group'>
            <div className='kick-stats-title'>{t('sessions.statAvgPullDirection')}</div>
            <div className={`kick-stats-title-avg${totalKicks > 0 ? '' : ' kick-stats-title-avg--placeholder'}`}>
              {t('sessions.statAvgLabel')}
            </div>
          </div>
          <div className='kick-stats-pull-ball'>
            <PullDirectionBall pullDirection={avgPullDirection ?? null} spinAxis={avgSpinAxis ?? null} />
          </div>
          <div className='kick-stats-pull-value'>
            {avgPullDirection != null ? `${Math.round(avgPullDirection)} ${degrees}` : '—'}
          </div>
        </div>
      )}

      {!compactMode && showWallTile && renderTile(
        t('sessions.statAvgWallCrossing'),
        valueUnit(avgWallCrossingHeight ?? null, t('common.unitMeters'), 2),
        totalKicks > 0,
      )}
    </div>
  );
};

export default KickStatsSummaryRow;
